/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
/**
 * Single source of truth for bid request project team roles.
 *
 * Everything that used to be a separate role-keyed table — the display label, the Salesforce
 * lookup and id fields, the team approval a role owns, and the inputs it must complete when a bid
 * request is created — now lives in one entry per role. Adding a role means adding one entry
 * here; `satisfies` makes every derived table a compile error until that entry is complete.
 *
 * This module deliberately imports nothing, so it stays a leaf that both `./bid-requests` and
 * `../salesforce/projects` can depend on without a cycle.
 */
export const bidRequestInputTeamApprovals = [
    'dev_approval',
    'procurement_approval',
    'eng_approval',
    'finance_approval',
    'txm_approval',
];
/**
 * Declaration order is the canonical role order; it drives `objectKeys(BidRequestProjectTeam)`,
 * which the UI uses to render the notification audience picker.
 */
export const bidRequestProjectTeamRoles = {
    dev_lead: {
        label: 'Lead Developer',
        sfLookupField: 'Lead_Developer__r',
        sfIdField: 'Lead_Developer__c',
        approval: 'dev_approval',
        createdInputs: 'development inputs',
    },
    dev: {
        label: 'Developer',
        sfLookupField: 'Developer__r',
        sfIdField: 'Developer__c',
        approval: null,
        createdInputs: null,
    },
    dev_engineer: {
        label: 'PV Engineer',
        sfLookupField: 'Dev_Engineer__r',
        sfIdField: 'Dev_Engineer__c',
        approval: 'eng_approval',
        createdInputs: 'eng inputs',
    },
    bess_lead: {
        label: 'BESS Lead',
        sfLookupField: 'BESS_Lead__r',
        sfIdField: 'BESS_Lead__c',
        approval: null,
        createdInputs: 'eng inputs',
    },
    land_lead: {
        label: 'Land Lead',
        sfLookupField: 'Land_Lead__r',
        sfIdField: 'Land_Lead__c',
        approval: null,
        createdInputs: null,
    },
    permit_lead: {
        label: 'Permit Lead',
        sfLookupField: 'Permit_Lead__r',
        sfIdField: 'Permit_Lead__c',
        approval: null,
        createdInputs: null,
    },
    txm_planner: {
        label: 'TXM Planner',
        sfLookupField: 'TXM_Planner__r',
        sfIdField: 'TXM_Planner__c',
        approval: 'txm_approval',
        createdInputs: 'TXM inputs',
    },
    eng_lead: {
        label: 'Eng Lead',
        sfLookupField: 'Eng_Lead__r',
        sfIdField: 'Eng_Lead__c',
        approval: null,
        createdInputs: null,
    },
    finance_lead: {
        label: 'Finance Lead',
        sfLookupField: 'Finance_Lead__r',
        sfIdField: 'Finance_Lead__c',
        approval: 'finance_approval',
        // Finance reviews the team's inputs once approved, so it is not prompted on creation.
        createdInputs: null,
    },
    procurement_lead: {
        label: 'Procurement Lead',
        sfLookupField: 'Procurement_Lead__r',
        sfIdField: 'Procurement_Lead__c',
        approval: 'procurement_approval',
        createdInputs: 'procurement inputs',
    },
    origination_lead: {
        label: 'Origination Lead',
        sfLookupField: 'Origination_Lead__r',
        sfIdField: 'Origination_Lead__c',
        approval: null,
        createdInputs: null,
    },
};
export const bidRequestProjectTeamRoleKeys = Object.keys(bidRequestProjectTeamRoles);
/**
 * Role key -> display label. Retains the shape of the enum it replaced, so `BidRequestProjectTeam.dev_lead`
 * and `objectKeys(BidRequestProjectTeam)` keep working unchanged.
 */
export const BidRequestProjectTeam = Object.fromEntries(bidRequestProjectTeamRoleKeys.map((role) => [role, bidRequestProjectTeamRoles[role].label]));
/** Salesforce lookup field -> role label. */
export const sfRolesMap = Object.fromEntries(bidRequestProjectTeamRoleKeys.map((role) => [
    bidRequestProjectTeamRoles[role].sfLookupField,
    bidRequestProjectTeamRoles[role].label,
]));
/** Role key -> the Salesforce lookup field it is hydrated from. */
export const roleToSfLookupField = Object.fromEntries(bidRequestProjectTeamRoleKeys.map((role) => [
    role,
    bidRequestProjectTeamRoles[role].sfLookupField,
]));
/** Role key -> the Salesforce user id field it is read from. */
export const roleToSfIdField = Object.fromEntries(bidRequestProjectTeamRoleKeys.map((role) => [role, bidRequestProjectTeamRoles[role].sfIdField]));
/** Team approval -> the roles that own it. */
export const approvalToRoleMap = bidRequestInputTeamApprovals.reduce((acc, approval) => {
    acc[approval] = bidRequestProjectTeamRoleKeys.filter((role) => bidRequestProjectTeamRoles[role].approval === approval);
    return acc;
}, {});
/** Roles prompted to complete inputs when a bid request is created. */
export const rolesPromptedOnBidRequestCreation = bidRequestProjectTeamRoleKeys.filter((role) => bidRequestProjectTeamRoles[role].createdInputs !== null);
//# sourceMappingURL=project-team-roles.js.map